/**
 * Swipe View — adjacent pre-warm policy (SMASH-1979, spec §4.4).
 *
 * The spec's §4.4 "Adjacent pre-warm" defines two conformance levels for a ±1
 * neighbouring slide's media, plus one hard suppression. This module is the
 * decision — which level applies right now — and nothing else: no React, no
 * DOM, no adapter knowledge, mirroring slotWindow.js's shape so the policy is
 * unit-testable without mounting anything or faking a media element.
 *
 * Level A (the floor, normative) is metadata: the element exists, dimensions
 * and first-frame readiness are fetched, the payload is not. Level B (a MAY) is
 * buffering ahead of metadata, and is permitted ONLY when the connection is
 * plausibly fast. Save-Data suppresses pre-warm entirely — no element at all,
 * Level A included.
 *
 * The two rules that are easy to get backwards, both stated normatively in the
 * spec and both encoded below:
 *
 *   - **Absence of the signal is not permission.** Where the Network
 *     Information API is unavailable, an implementation MUST stay at Level A —
 *     it does NOT get Level B for want of evidence against it, and it is NOT
 *     suppressed either. The unknown case resolves to the cheaper behaviour,
 *     never the more expensive one, which is the same fail-safe direction §2.4
 *     takes on suppression.
 *   - **`effectiveType` separates fast from slow, not metered from unmetered.**
 *     A fast cellular connection reads `4g`. Save-Data is the only signal of
 *     metered *intent* the platform offers, which is why it is a hard
 *     suppression here rather than one input among several.
 *
 * The level values are deliberately the `HTMLMediaElement.preload` values they
 * authorise, so a caller hands the level straight to the adapter instead of
 * translating it through a second table that could disagree with this one.
 * `PREWARM_SUPPRESSED` is not a preload value precisely because it authorises
 * no element to exist.
 */

/** Save-Data is on: no media element is created for a neighbour at all. */
export const PREWARM_SUPPRESSED = 'suppressed';
/** Level A — the normative floor. Metadata is the ceiling. */
export const PREWARM_METADATA = 'metadata';
/** Level B — a MAY, gated on a plausibly-fast connection. */
export const PREWARM_BUFFERED = 'auto';

/**
 * The level a given Network Information API object authorises.
 *
 * Pure, and takes the connection rather than reading `navigator` itself, so
 * every branch is reachable in a test without mutating a global — including the
 * awkward middle cases (the API present but reporting no `saveData`, or a
 * `saveData` of `false` on a slow link) that the two-condition Level B gate
 * exists to separate.
 */
export function prewarmLevelFor(connection) {
    // No API: Level A. Not Level B, and not suppressed.
    if (!connection) return PREWARM_METADATA;
    // Metered intent, stated by the visitor. Hard stop, checked FIRST so it
    // cannot be reached past a Level B decision.
    if (connection.saveData === true) return PREWARM_SUPPRESSED;
    // "Plausibly fast" is BOTH conditions. `=== false` rather than a falsy
    // check is the point: an API that reports no `saveData` at all has told us
    // nothing, and silence is not the same answer as "not saving data".
    if (connection.saveData === false && connection.effectiveType === '4g') return PREWARM_BUFFERED;
    return PREWARM_METADATA;
}

/**
 * `prewarmLevelFor` against the live API, read at the moment a pre-warm is
 * about to happen rather than cached at open time — a visitor can turn Save-Data
 * on, or walk out of wifi, in the middle of a session, and the next warm should
 * respect that rather than a snapshot taken minutes earlier.
 *
 * Guarded because this runs inside a WordPress front end on hosts we do not
 * control: a `navigator` shim that throws on an unknown property would
 * otherwise take the whole viewer down, and the fail-safe answer to "we could
 * not read the connection" is the same as the answer to "there is no API" —
 * Level A.
 */
export function currentPrewarmLevel() {
    try {
        return prewarmLevelFor(typeof navigator === 'undefined' ? null : navigator.connection);
    } catch (e) {
        return PREWARM_METADATA;
    }
}
